import React, { ReactElement, useMemo, useReducer } from 'react';
import {
  FreeItemsModalProps,
  GenericModalProps,
  GlobalAppContextMenuInterface,
  LoginModalProps,
  MenuItemModalProps,
  MobileAppSignupBonusModalProps,
  OfferModalProps,
  OrderBasketModalProps,
  OrderMethodModalProps,
  OtpVerificationModalProps,
  ReducerDispatchActionPayloadInterface,
  RestaurantClosedModalProps,
} from '../services/exports/Interfaces';
import { APP_STATE_REDUCER_TYPES } from '../services/exports/Constants';
import { ConfirmCancelModalProps } from '../modals/ConfirmCancelModal';

interface Props {
  children: any;
}

export interface GlobalAppStateContextInterface {
  loginModalProps: LoginModalProps;
  infoModalProps: ConfirmCancelModalProps;
  menu: Partial<GlobalAppContextMenuInterface>;
  menuItemModalProps: MenuItemModalProps;
  orderMethodModalProps: OrderMethodModalProps;
  otpVerificationModalProps: OtpVerificationModalProps;
  restaurantClosedModalProps: RestaurantClosedModalProps;
  orderBasketModalProps: OrderBasketModalProps;
  eligibleOffersModalProps: OrderBasketModalProps;
  offerModalProps: OfferModalProps;
  freeItemsModalProps: FreeItemsModalProps;
  mobileAppSignupBonusProps: MobileAppSignupBonusModalProps;
  addPromoOrGiftCardProps: GenericModalProps;
  buyGiftCardProps: GenericModalProps;
}

export interface GlobalAppContextReducerHookInterface {
  state: GlobalAppStateContextInterface;
  dispatch: (
    data: ReducerDispatchActionPayloadInterface<APP_STATE_REDUCER_TYPES, Partial<GlobalAppStateContextInterface>>,
  ) => void;
}

export const AppStateContext: React.Context<GlobalAppContextReducerHookInterface> = React.createContext(null);

export default function AppStateProvider(props: Props): ReactElement {
  const { children } = props;

  const initialState: GlobalAppStateContextInterface = {
    loginModalProps: {
      openModal: false,
      isStatic: false,
      initialPhoneNumber: undefined,
    },
    infoModalProps: {
      openModal: false,
    },
    menuItemModalProps: {
      openModal: false,
      product: null,
    },
    menu: {
      screenPosition: 0,
      activeCategoryId: null,
      menuLoaded: false,
    },
    orderMethodModalProps: {
      openModal: false,
    },
    otpVerificationModalProps: {
      openModal: false,
    },
    restaurantClosedModalProps: {
      openModal: false,
    },
    orderBasketModalProps: {
      openModal: false,
    },
    eligibleOffersModalProps: {
      openModal: false,
    },
    offerModalProps: {
      openModal: false,
      offer: null,
    },
    freeItemsModalProps: {
      openModal: false,
      offer: null,
    },
    mobileAppSignupBonusProps: {
      openModal: false,
    },
    addPromoOrGiftCardProps: {
      openModal: false,
    },
    buyGiftCardProps: {
      openModal: false,
    },
  };

  const [state, dispatch] = useReducer<
    (
      prevState: GlobalAppStateContextInterface,
      action: ReducerDispatchActionPayloadInterface<APP_STATE_REDUCER_TYPES, Partial<GlobalAppStateContextInterface>>,
    ) => any
  >(
    (
      prevState: GlobalAppStateContextInterface,
      action: ReducerDispatchActionPayloadInterface<APP_STATE_REDUCER_TYPES, Partial<GlobalAppStateContextInterface>>,
    ) => {
      const { payload } = action;
      switch (action.type) {
        case APP_STATE_REDUCER_TYPES.TOGGLE_LOGIN_MODAL:
          return {
            ...prevState,
            loginModalProps: payload?.loginModalProps,
          };
        case APP_STATE_REDUCER_TYPES.TOGGLE_INFO_MODAL:
          return {
            ...prevState,
            infoModalProps: payload?.infoModalProps,
          };
        case APP_STATE_REDUCER_TYPES.SAVE_MENU:
          return {
            ...prevState,
            menu: {
              ...prevState.menu,
              ...payload.menu,
            },
          };
        case APP_STATE_REDUCER_TYPES.TOGGLE_MENU_ITEM_MODAL:
          return {
            ...prevState,
            menuItemModalProps: payload?.menuItemModalProps,
          };
        case APP_STATE_REDUCER_TYPES.TOGGLE_ORDER_METHOD_MODAL:
          return {
            ...prevState,
            orderMethodModalProps: payload?.orderMethodModalProps,
          };
        case APP_STATE_REDUCER_TYPES.TOGGLE_OTP_VERIFICATION_MODAL:
          return {
            ...prevState,
            otpVerificationModalProps: payload?.otpVerificationModalProps,
          };
        case APP_STATE_REDUCER_TYPES.TOGGLE_RESTAURANT_CLOSED_MODAL:
          return {
            ...prevState,
            restaurantClosedModalProps: payload?.restaurantClosedModalProps,
          };
        case APP_STATE_REDUCER_TYPES.TOGGLE_ORDER_BASKET_MODAL:
          return {
            ...prevState,
            orderBasketModalProps: payload?.orderBasketModalProps,
          };
        case APP_STATE_REDUCER_TYPES.TOGGLE_ELIGIBLE_OFFERS_MODAL:
          return {
            ...prevState,
            eligibleOffersModalProps: payload?.eligibleOffersModalProps,
          };
        case APP_STATE_REDUCER_TYPES.TOGGLE_OFFER_MODAL:
          return {
            ...prevState,
            offerModalProps: payload?.offerModalProps,
          };
        case APP_STATE_REDUCER_TYPES.TOGGLE_FREE_ITEMS_MODAL:
          return {
            ...prevState,
            freeItemsModalProps: payload?.freeItemsModalProps,
          };
        case APP_STATE_REDUCER_TYPES.TOGGLE_MOBILE_APP_SIGNUP_BONUS_MODAL:
          return {
            ...prevState,
            mobileAppSignupBonusProps: payload?.mobileAppSignupBonusProps,
          };
        case APP_STATE_REDUCER_TYPES.TOGGLE_ADD_PROMO_OR_GIFT_CARD_MODAL:
          return {
            ...prevState,
            addPromoOrGiftCardProps: payload?.addPromoOrGiftCardProps,
          };
        case APP_STATE_REDUCER_TYPES.TOGGLE_BUY_GIFT_CARD_MODAL:
          return {
            ...prevState,
            buyGiftCardProps: payload?.buyGiftCardProps,
          };
      }
    },
    initialState,
  );

  const context = useMemo(
    () => ({
      state,
      dispatch,
    }),
    [state, dispatch],
  );

  return <AppStateContext.Provider value={context}>{children}</AppStateContext.Provider>;
}
